import React, { useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Paper, Typography, Box } from '@mui/material';
import dayjs from 'dayjs';
import { useScenarios } from '../../context/ScenarioContext';
import { useBioData } from '../../context/BioDataContext';
import './PromotionsManager.css';

function PromotionsManager({ scenario }) {
  const { addPromotion, removePromotion } = useScenarios();
  const { bioData } = useBioData();
  const gradeInputRef = useRef(null);
  const [selectedPromotionYear, setSelectedPromotionYear] = useState(null);
  const [error, setError] = useState(''); // State for error message
  const [yearError, setYearError] = useState(false); // State for year input error class
  const [gradeError, setGradeError] = useState(false); // State for grade input error class
  const currentYear = new Date().getFullYear();
  const [showHelp, setShowHelp] = useState(false); // State to toggle help text

  const handleAddPromotion = () => {
    // Clear previous errors
    setError('');
    setYearError(false);
    setGradeError(false);

    const gradeInput = gradeInputRef.current;
    let isValid = true;

    // --- Validation ---
    let year = null;
    let grade = null;

    if (!selectedPromotionYear) {
        setError("Please select a promotion year.");
        setYearError(true);
        isValid = false;
    } else {
        year = selectedPromotionYear.year(); // Get year from dayjs object
    }

    if (!gradeInput || !gradeInput.value) {
        if(isValid) setError("Please select a new grade."); // Only show if year was okay
        setGradeError(true);
        isValid = false;
    } else {
        grade = parseInt(gradeInput.value);
        if (isNaN(grade)) {
            // Should not happen with select, but check anyway
            setError("Invalid grade selected.");
            setGradeError(true);
            isValid = false;
        }
    }

    if (!isValid) return; // Stop if basic validation failed

    // Further Validation
    if (year < currentYear) {
      setError("Promotion year cannot be in the past.");
      setYearError(true); 
      isValid = false;
    }
    
    const endYearCheck = scenario.pensionType === 'deferred' ? scenario.departureYear : scenario.retirementYear;
    if (endYearCheck && year >= endYearCheck) {
       setError(`Promotion year must be before ${scenario.pensionType === 'deferred' ? 'departure' : 'retirement'} year (${endYearCheck}).`);
       setYearError(true); 
       isValid = false;
    }
    
    if (scenario.promotions.some(p => p.year === year)) {
       setError(`A promotion already exists for the year ${year}.`);
       setYearError(true); 
       isValid = false;
    }

    // Check grade progression
    let gradeAtPromotionYear = bioData.currentGrade || 0; // Start with initial grade
     const promotionsBefore = scenario.promotions
        .filter(p => p.year < year)
        .sort((a, b) => a.year - b.year); // Sort to get the latest one before target year
     if(promotionsBefore.length > 0){
         gradeAtPromotionYear = promotionsBefore[promotionsBefore.length - 1].grade;
     }
    if (grade <= gradeAtPromotionYear) {
        setError(`New grade (${grade === 16 ? 'SES' : `GS-${grade}`}) must be higher than the grade in effect (${gradeAtPromotionYear === 16 ? 'SES' : `GS-${gradeAtPromotionYear}`}) before year ${year}.`);
        setGradeError(true); 
        isValid = false;
    }

    if (!isValid) return; // Stop if any validation failed

    // --- Add Promotion ---
    addPromotion(scenario.id, year, grade);

    // Clear inputs after successful add
    setSelectedPromotionYear(null); // Clear the DatePicker
    gradeInput.value = ''; // Reset grade select
  };

  // Sort promotions by year for display
  const sortedPromotions = [...scenario.promotions].sort((a, b) => a.year - b.year);

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
      <div className="promotions-header">
        <Typography variant="h6" sx={{ color: '#34517a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          🚀 Expected Promotions for {scenario.name}
        </Typography>
        <button 
          className="help-toggle" 
          onClick={() => setShowHelp(!showHelp)} 
          type="button"
          aria-label={showHelp ? "Hide help" : "Show help"}
        >
          {showHelp ? "Hide Help" : "Help"}
        </button>
      </div>
      
      {showHelp && (
        <div className="promotions-help">
          <p>
            <strong>About promotions in this scenario:</strong> Each scenario can have its own set of expected future promotions. 
            When you create a new scenario, it will initially copy promotions from the previous scenario, but you can then
            modify them independently.
          </p>
          <p>
            <strong>How to use:</strong> Add expected future promotions by selecting a year and new grade, then clicking "Add Promotion".
            Each promotion should reflect when you expect to reach a higher grade level.
          </p>
          <p>
            <strong>Note:</strong> Promotions must be in chronological order with increasing grades and before retirement/departure year.
          </p>
        </div>
      )}
      
      <div className="add-promotion">
        <div className="form-row">
          <div className="form-group compact">
            <DatePicker
              label="Promotion Year"
              views={['year']}
              value={selectedPromotionYear}
              onChange={(newValue) => {
                setSelectedPromotionYear(newValue);
                if (newValue) { // Clear error if a value is selected
                    setYearError(false);
                }
              }}
              minDate={dayjs().set('year', currentYear)} // Min current year
              maxDate={scenario.pensionType === 'deferred' 
                ? (scenario.departureYear ? dayjs().set('year', scenario.departureYear - 1) : undefined) 
                : (scenario.retirementYear ? dayjs().set('year', scenario.retirementYear - 1) : undefined)}
              slotProps={{
                textField: {
                  id: `promotion-year-${scenario.id}`,
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  error: yearError,
                  helperText: yearError ? (error.toLowerCase().includes('year') ? error : '') : ''
                }
              }}
            />
          </div>
          <div className="form-group compact">
            <label htmlFor={`promotion-grade-${scenario.id}`}>New Grade:</label>
            <select
              id={`promotion-grade-${scenario.id}`}
              ref={gradeInputRef}
              className={gradeError ? 'error' : ''}
            >
              <option value="">Select Grade</option>
              <option value="16">SES</option>
              <option value="15">GS-15</option>
              <option value="14">GS-14</option>
              <option value="13">GS-13</option>
              <option value="12">GS-12</option>
              <option value="11">GS-11</option>
              <option value="10">GS-10</option>
              <option value="9">GS-9</option>
              <option value="8">GS-8</option>
              <option value="7">GS-7</option>
              <option value="6">GS-6</option>
              <option value="5">GS-5</option>
              <option value="4">GS-4</option>
              <option value="3">GS-3</option>
              <option value="2">GS-2</option>
              <option value="1">GS-1</option>
            </select>
            {gradeError && error.toLowerCase().includes('grade') && <div className="error-message">{error}</div>}
          </div>
          <div className="form-group compact add-button-container">
            <button 
              className="add-button" 
              onClick={handleAddPromotion} 
              type="button"
            >
              Add Promotion
            </button>
          </div>
        </div>
        {/* Display error message if it doesn't fit into year or grade specific errors */}
        {error && !error.toLowerCase().includes('year') && !error.toLowerCase().includes('grade') && (
          <div className="error-message general-error">{error}</div>
        )}
      </div>
      
      {sortedPromotions.length > 0 ? (
        <div className="promotions-list">
          <h4>Promotions in {scenario.name}:</h4>
          <table className="promotions-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Grade</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedPromotions.map((promotion, index) => (
                <tr key={`${scenario.id}-${promotion.year}-${promotion.grade}`}>
                  <td>{promotion.year}</td>
                  <td>{promotion.grade === 16 ? 'SES' : `GS-${promotion.grade}`}</td>
                  <td>
                    <button 
                      className="remove-button" 
                      onClick={() => removePromotion(scenario.id, index)}
                      type="button"
                      aria-label={`Remove ${promotion.year} promotion to ${promotion.grade === 16 ? 'SES' : `GS-${promotion.grade}`}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-promotions">No promotions defined for this scenario.</p>
      )}
    </Paper>
  );
}

export default PromotionsManager;
